/**
 * Optimized HomeTestimonials Component
 * Performance improvements:
 * - React.memo for preventing unnecessary re-renders
 * - useMemo for static config and computed values
 * - useCallback for event handlers
 * - Intersection Observer for view tracking
 * - Analytics tracking for views, navigation, and clicks
 */

'use client';

import { memo, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import Image from 'next/image';
import { testimonialsConfig } from "@/constant/home";
import { useIntersectionObserver, useAnalytics } from '@/hooks/usePerformance';

function HomeTestimonialsComponent() {
    const [current, setCurrent] = useState(0);
    const sectionRef = useRef<HTMLDivElement>(null);
    const hasTrackedView = useRef(false);
    const { trackEvent } = useAnalytics();

    // Memoize config to prevent re-creation
    const config = useMemo(() => testimonialsConfig, []);
    const { title, subtitle, testimonials } = config;

    // Memoize computed values
    const slidesToShow = 3;
    const totalDots = useMemo(() =>
        Math.ceil(testimonials.length / slidesToShow),
        [testimonials.length]
    );

    // Track section visibility
    const isInView = useIntersectionObserver(sectionRef, {
        threshold: 0.2,
        rootMargin: '50px',
    });

    // Track when section becomes visible (only once)
    useEffect(() => {
        if (isInView && !hasTrackedView.current) {
            trackEvent('testimonials_section_view', {
                section_title: title,
                total_testimonials: testimonials.length,
            });
            hasTrackedView.current = true;
        }
    }, [isInView, title, testimonials.length, trackEvent]);

    // Memoized navigation handlers with analytics
    const prevSlide = useCallback(() => {
        setCurrent((prev) => {
            const newIndex = prev === 0
                ? (totalDots - 1) * slidesToShow
                : prev - slidesToShow;

            trackEvent('testimonial_nav_click', {
                direction: 'prev',
                from_index: prev,
                to_index: newIndex,
            });

            return newIndex;
        });
    }, [totalDots, slidesToShow, trackEvent]);

    const nextSlide = useCallback(() => {
        setCurrent((prev) => {
            const newIndex = prev + slidesToShow >= testimonials.length
                ? 0
                : prev + slidesToShow;

            trackEvent('testimonial_nav_click', {
                direction: 'next',
                from_index: prev,
                to_index: newIndex,
            });

            return newIndex;
        });
    }, [testimonials.length, slidesToShow, trackEvent]);

    // Dot click handler with analytics
    const handleDotClick = useCallback((idx: number) => {
        const newIndex = idx * slidesToShow;
        setCurrent(newIndex);

        trackEvent('testimonial_dot_click', {
            dot_index: idx,
            slide_index: newIndex,
        });
    }, [slidesToShow, trackEvent]);
    return (
        <section ref={sectionRef} className="section_testimonials">
            <div className="padding-global">
                <div className="container-large">
                    <div className="padding-section-large">
                        <div className="testimonials2_component">
                            <div className="testimonials2_content-left">
                                <div className="testimonials2_header-wrapper">
                                    <h2 className="heading-style-h2 text-[var(--primary--burgundy)] dark:text-[var(--primary--pink)]">
                                        {title}
                                    </h2>
                                    <div className="spacer-small"></div>
                                    <p>{subtitle}</p>
                                </div>
                            </div>
                            <div className="testimonials2_content-right">
                                <div className="testimonials2_slider w-slider">
                                    <div
                                        className="testimonials2_mask w-slider-mask"
                                        style={{
                                            overflow: "hidden",
                                            width: "100%",
                                        }}
                                    >
                                        <div
                                            className="testimonials2_slides-track"
                                            style={{
                                                display: "flex",
                                                transition: "transform 0.5s cubic-bezier(.77,0,.18,1)",
                                                transform: `translateX(-${(current / testimonials.length) * 100}%)`,
                                                width: `${(testimonials.length / slidesToShow) * 100}%`,
                                            }}
                                        >
                                            {testimonials.map((t, idx) => (
                                                <div
                                                    className="testimonials2_slide w-slide"
                                                    key={idx}
                                                    style={{
                                                        width: `${100}%`,
                                                        // width: `${100 / testimonials.length}%`,
                                                        // flex: `0 0 ${100 / testimonials.length}%`,
                                                        flex: `0 0 ${100}%`,
                                                        boxSizing: "border-box",
                                                    }}
                                                >
                                                    <div className="testimonials_item p-4 rounded-lg bg-[var(--primary--beige)] dark:bg-[var(--gray--100)]">
                                                        <div className="testimonials_content-wrapper">
                                                            <StarIcons stars={t.stars} />
                                                            <div className="testimonials_quote">
                                                                <p className="text-weight-medium line-clamp-7">{t.quote}</p>
                                                                <div className="padding-bottom padding-xxsmall"></div>
                                                                {/* <a href={t.reviewLink.href} className="link text-size-small w-inline-block">
                                                                    <div className="link_content-wrapper">
                                                                        <div className="link_text">{t.reviewLink.text}</div>
                                                                        <div className="link_icon w-embed">
                                                                            <svg aria-hidden="true" role="img" xmlns="http://www.w3.org/2000/svg"
                                                                                width="12" height="12" viewBox="0 0 12 12">
                                                                                <g fill="currentColor">
                                                                                    <path
                                                                                        d="M1.293,10.707a1,1,0,0,0,1.414,0L7.854,5.561l2.292,2.293A.5.5,0,0,0,10.5,8a.489.489,0,0,0,.191-.038A.5.5,0,0,0,11,7.5v-6a.5.5,0,0,0-.5-.5h-6a.5.5,0,0,0-.354.854L6.439,4.146,1.293,9.293A1,1,0,0,0,1.293,10.707Z"
                                                                                        fill="currentColor"></path>
                                                                                </g>
                                                                            </svg>
                                                                        </div>
                                                                    </div>
                                                                </a> */}
                                                            </div>
                                                        </div>
                                                        <div className="testimonials_author">
                                                            <div className="author_component">
                                                                <div className="author_image-wrapper">
                                                                    <Image unoptimized width={48} height={48} alt={t.author.name} src={t.author.image} className="author_image" />
                                                                </div>
                                                                <div className="author_content-wrapper">
                                                                    <div className="text-size-small text-weight-medium">{t.author.name}</div>
                                                                    {/* <div className="text-size-tiny text-color-gray-70">{t.author.title}</div> */}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="testimonials2_slider-arrow is-bottom-previous w-slider-arrow-left"
                                        onClick={prevSlide}
                                        aria-label="Previous testimonials"
                                    >
                                        <div className="testimonials2_slider-arrow-icon w-embed">
                                            <svg role="img" xmlns="http://www.w3.org/2000/svg" width="16"
                                                height="16" viewBox="0 0 16 16">
                                                <title>Back icon</title>
                                                <g fill="currentColor">
                                                    <path fill="currentColor"
                                                        d="M7,9h8c0.6,0,1-0.4,1-1v0c0-0.6-0.4-1-1-1H7V2L0,8l7,6V9z">
                                                    </path>
                                                </g>
                                            </svg>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        className="testimonials2_slider-arrow is-bottom-next w-slider-arrow-right"
                                        onClick={nextSlide}
                                        aria-label="Next testimonials"
                                    >
                                        <div className="testimonials2_slider-arrow-icon w-embed">
                                            <svg role="img" xmlns="http://www.w3.org/2000/svg" width="16"
                                                height="16" viewBox="0 0 16 16">
                                                <title>Next icon</title>
                                                <g fill="currentColor">
                                                    <path fill="currentColor"
                                                        d="M9,7H1C0.4,7,0,7.4,0,8v0c0,0.6,0.4,1,1,1h8v5l7-6L9,2V7z">
                                                    </path>
                                                </g>
                                            </svg>
                                        </div>
                                    </button>
                                    <div className="testimonials2_slide-nav w-slider-nav w-slider-nav-invert w-round">
                                        {Array.from({ length: totalDots }).map((_, idx) => (
                                            <button
                                                type="button"
                                                className={`w-slider-dot${idx === Math.floor(current / slidesToShow) ? " w-active" : ""}`}
                                                aria-label={`Show slide ${idx + 1} of ${totalDots}`}
                                                aria-pressed={idx === Math.floor(current / slidesToShow)}
                                                key={idx}
                                                onClick={() => handleDotClick(idx)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

// Memoized star rating component
const StarIcons = memo(function StarIcons({ stars }: { stars: number }) {
    const fullStars = Math.floor(stars);
    const halfStar = stars % 1 >= 0.5;

    // Memoize star array
    const starElements = useMemo(() => {
        const elements = [];

        // Full stars
        for (let i = 0; i < fullStars; i++) {
            elements.push(
                <div className="testimonials_star-icon w-embed" key={`star-${i}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16">
                        <title>Star icon</title>
                        <g fill="currentColor">
                            <polygon
                                points="10 6 8 0.256 6 6 0 6 4.89 9.828 2.968 15.744 8 12.088 13.032 15.744 11.11 9.828 16 6 10 6"
                                fill="currentColor"
                            />
                        </g>
                    </svg>
                </div>
            );
        }

        // Half star
        if (halfStar) {
            elements.push(
                <div className="testimonials_star-icon w-embed" key="star-half">
                    <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 6L8 .256v11.832l-5.032 3.656L4.89 9.828 0 6h6z" fill="currentColor" />
                        <path d="M10 6L8 .256v11.832l5.032 3.656-1.922-5.916L16 6h-6z" fill="#f6cbe1" />
                    </svg>
                </div>
            );
        }

        return elements;
    }, [fullStars, halfStar]);

    return <div className="testimonials_stars">{starElements}</div>;
});

// Export memoized component
export const HomeTestimonials = memo(HomeTestimonialsComponent);
HomeTestimonials.displayName = 'HomeTestimonials';

// Default export for backward compatibility
export default HomeTestimonials;